import React, { useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Chip,
  CircularProgress,
  Grid2,
  IconButton,
  Paper,
  Switch,
  Tooltip,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import { observer, useLocalObservable } from "mobx-react";
import { runInAction } from "mobx";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import CustomTextField from "../../../../../views/components/common/textFields/TextField";
import AppSearchDropdownMulti from "../../../../../views/components/common/filter/AppSearchDropdownMulti";
import PermissionBlockLabel from "../../../../../views/components/common/pop-up/PermissionBlockLabel ";
import ConfirmationPopup from "../../../../../views/components/common/pop-up/ConfirmationPopup";
import { useStores } from "../../../use-stores";
import { useNotification } from "../../../../../utils/NotificationsContext";
import { EmployeeRoles } from "../../../../../models/Employees/EmployeeRoles";
import { CatalogOptionsStore } from "../../../stores/CatalogOptionsStore";
import { FilterValue } from "../../../../../models/Filters/FilterValue";
import {
  CreateOptionValueBody,
  DISPLAY_TYPE_OPTIONS,
  DisplayType,
  OptionValueApi,
  UpdateOptionBody,
  UpdateOptionValueBody,
  getApiErrorMessage,
  getMetaHex,
  isDisplayType,
  validateCreateOptionValueBody,
  validateUpdateOptionBody,
} from "../../../../../models/CatalogOptions/types";
import { formatDate } from "../../../../../views/components/common/functions/formatDate";

const DISPLAY_TYPE_LIST: FilterValue[] = DISPLAY_TYPE_OPTIONS.map((o) => ({
  id: o.value,
  value: o.label,
}));

interface ValueDraft {
  value: string;
  sku_code: string;
  position: string;
  hex: string;
  is_active: boolean;
}

const emptyDraft = (): ValueDraft => ({
  value: "",
  sku_code: "",
  position: "0",
  hex: "",
  is_active: true,
});

const draftFromValue = (v: OptionValueApi): ValueDraft => ({
  value: v.value ?? "",
  sku_code: v.sku_code ?? "",
  position: String(v.position ?? 0),
  hex: getMetaHex(v.meta) ?? "",
  is_active: Boolean(v.is_active),
});

const CatalogOptionDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const { userStore } = useStores();
  const { showError, showSuccess } = useNotification();
  const catalogOptionsStore = useRef(new CatalogOptionsStore()).current;

  const optionId = Number(id);
  const canView = userStore.hasPermission([
    EmployeeRoles.VIEW_CATALOG,
    EmployeeRoles.EDIT_CATALOG,
  ]);
  const canEdit = userStore.hasPermission(EmployeeRoles.EDIT_CATALOG, false);
  const colors = theme.palette.customColors;

  const page = useLocalObservable(() => ({
    loading: true,
    savingOption: false,
    savingValue: false,
    deleteOptionOpen: false,
    deleteValueOpen: false,
    deletingValue: null as OptionValueApi | null,
    // option form
    name: "",
    slug: "",
    display_type: "dropdown" as DisplayType,
    position: "0",
    is_active: true,
    dirty: false,
    // values UI
    showAddForm: false,
    addDraft: emptyDraft() as ValueDraft,
    editingValueId: null as number | null,
    editDraft: emptyDraft() as ValueDraft,
    hydrateFromDetail() {
      const d = catalogOptionsStore.optionDetail;
      if (!d) return;
      this.name = d.name ?? "";
      this.slug = d.slug ?? "";
      this.display_type = isDisplayType(String(d.display_type))
        ? (d.display_type as DisplayType)
        : "dropdown";
      this.position = String(d.position ?? 0);
      this.is_active = Boolean(d.is_active);
      this.dirty = false;
      this.showAddForm = false;
      this.addDraft = emptyDraft();
      this.editingValueId = null;
      this.editDraft = emptyDraft();
    },
    markDirty() {
      this.dirty = true;
    },
  }));

  const detail = catalogOptionsStore.optionDetail;
  const isSwatch = page.display_type === "swatch";

  const loadDetail = async () => {
    if (!optionId || Number.isNaN(optionId)) {
      showError("Error", "Invalid option id.");
      navigate("/catalog-options/listing");
      return;
    }
    try {
      page.loading = true;
      await catalogOptionsStore.getOptionById(optionId);
      runInAction(() => page.hydrateFromDetail());
    } catch (error) {
      showError(
        "Error",
        getApiErrorMessage(error, "Failed to load option detail."),
      );
    } finally {
      page.loading = false;
    }
  };

  useEffect(() => {
    if (!canView) return;
    void loadDetail();
    return () => {
      catalogOptionsStore.clearOptionDetail();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optionId, canView]);

  const handleSaveOption = async () => {
    if (!canEdit || !detail || page.savingOption) return;

    const positionNum = parseInt(page.position, 10);
    const body: UpdateOptionBody = {
      name: page.name.trim(),
      slug: page.slug.trim() || undefined,
      display_type: page.display_type,
      position: Number.isNaN(positionNum) ? 0 : positionNum,
      is_active: page.is_active,
    };

    const validationError = validateUpdateOptionBody(body);
    if (validationError) {
      showError("Validation", validationError);
      return;
    }

    try {
      page.savingOption = true;
      await catalogOptionsStore.updateOption(detail.id, body);
      runInAction(() => page.hydrateFromDetail());
    } catch (error) {
      showError("Error", getApiErrorMessage(error, "Failed to update option."));
    } finally {
      page.savingOption = false;
    }
  };

  const handleDeleteOption = async () => {
    if (!detail || page.savingOption) return;
    try {
      page.savingOption = true;
      await catalogOptionsStore.deleteOption(detail.id);
      showSuccess("Option soft-deleted successfully.");
      page.deleteOptionOpen = false;
      await loadDetail();
    } catch (error) {
      showError("Error", getApiErrorMessage(error, "Failed to delete option."));
    } finally {
      page.savingOption = false;
    }
  };

  const buildValueBody = (
    draft: ValueDraft,
  ): CreateOptionValueBody | UpdateOptionValueBody => {
    const positionNum = parseInt(draft.position, 10);
    const body: CreateOptionValueBody = {
      value: draft.value.trim(),
      sku_code: draft.sku_code.trim(),
      position: Number.isNaN(positionNum) ? 0 : positionNum,
      is_active: draft.is_active,
    };
    if (isSwatch) {
      body.meta = draft.hex.trim() ? { hex: draft.hex.trim() } : null;
    }
    return body;
  };

  const handleCreateValue = async () => {
    if (!canEdit || !detail || page.savingValue) return;
    const body = buildValueBody(page.addDraft) as CreateOptionValueBody;
    const existingSkus = (detail.values ?? [])
      .filter((v) => v.is_active)
      .map((v) => v.sku_code ?? "")
      .filter(Boolean);
    const validationError = validateCreateOptionValueBody(body, existingSkus);
    if (validationError) {
      showError("Validation", validationError);
      return;
    }

    try {
      page.savingValue = true;
      await catalogOptionsStore.createValues(detail.id, body);
      runInAction(() => {
        page.showAddForm = false;
        page.addDraft = emptyDraft();
      });
      await loadDetail();
    } catch (error) {
      showError("Error", getApiErrorMessage(error, "Failed to add value."));
    } finally {
      page.savingValue = false;
    }
  };

  const handleUpdateValue = async (valueId: number) => {
    if (!canEdit || !detail || page.savingValue) return;
    const body = buildValueBody(page.editDraft) as UpdateOptionValueBody;

    if (!body.value?.toString().trim()) {
      showError("Validation", "Value is required.");
      return;
    }
    if (!body.sku_code?.toString().trim()) {
      showError("Validation", "SKU code is required.");
      return;
    }
    if ((body.value as string).length > 120) {
      showError("Validation", "Value must be at most 120 characters.");
      return;
    }
    if ((body.sku_code as string).length > 12) {
      showError("Validation", "SKU code must be at most 12 characters.");
      return;
    }

    const otherSkus = (detail.values ?? [])
      .filter((v) => v.id !== valueId && v.is_active)
      .map((v) => (v.sku_code ?? "").toLowerCase());
    if (otherSkus.includes((body.sku_code as string).toLowerCase())) {
      showError("Validation", `Duplicate SKU code "${body.sku_code}".`);
      return;
    }

    try {
      page.savingValue = true;
      await catalogOptionsStore.updateValue(detail.id, valueId, body);
      runInAction(() => {
        page.editingValueId = null;
        page.editDraft = emptyDraft();
      });
    } catch (error) {
      showError("Error", getApiErrorMessage(error, "Failed to update value."));
    } finally {
      page.savingValue = false;
    }
  };

  const handleDeleteValue = async () => {
    if (!detail || !page.deletingValue || page.savingValue) return;
    try {
      page.savingValue = true;
      await catalogOptionsStore.deleteValue(detail.id, page.deletingValue.id);
      showSuccess("Value soft-deleted successfully.");
      page.deleteValueOpen = false;
      page.deletingValue = null;
    } catch (error) {
      showError("Error", getApiErrorMessage(error, "Failed to delete value."));
    } finally {
      page.savingValue = false;
    }
  };

  const startEditValue = (row: OptionValueApi) => {
    page.editingValueId = row.id;
    page.editDraft = draftFromValue(row);
    page.showAddForm = false;
  };

  const cancelEditValue = () => {
    page.editingValueId = null;
    page.editDraft = emptyDraft();
  };

  const sectionSx = {
    p: { xs: 2, sm: 2.5 },
    borderRadius: 3,
    border: `1px solid ${colors?.greyBorder || theme.palette.divider}`,
    bgcolor: colors?.white || theme.palette.background.paper,
    boxShadow: colors?.shadowVeryLight || "none",
    mb: 2.5,
  };

  const renderValueFields = (
    draft: ValueDraft,
    onChange: (patch: Partial<ValueDraft>) => void,
    disabled: boolean,
  ) => (
    <Grid2 container spacing={1.5} columns={12}>
      <Grid2 size={{ xs: 12, sm: isSwatch ? 4 : 5, md: isSwatch ? 4 : 5 }}>
        <CustomTypography
          variant="body3"
          color={colors?.greyTextColor}
          sx={{ mb: 0.75 }}
        >
          Value *
        </CustomTypography>
        <CustomTextField
          width="100%"
          placeholder="Enter value"
          value={draft.value}
          disabled={disabled}
          onChange={(e) => onChange({ value: e.target.value || "" })}
        />
      </Grid2>
      <Grid2 size={{ xs: 12, sm: isSwatch ? 3 : 4, md: isSwatch ? 3 : 4 }}>
        <CustomTypography
          variant="body3"
          color={colors?.greyTextColor}
          sx={{ mb: 0.75 }}
        >
          SKU code *
        </CustomTypography>
        <CustomTextField
          width="100%"
          placeholder="e.g. RD"
          value={draft.sku_code}
          disabled={disabled}
          onChange={(e) => onChange({ sku_code: e.target.value || "" })}
        />
      </Grid2>
      <Grid2 size={{ xs: 12, sm: 3, md: 3 }}>
        <CustomTypography
          variant="body3"
          color={colors?.greyTextColor}
          sx={{ mb: 0.75 }}
        >
          Position
        </CustomTypography>
        <CustomTextField
          width="100%"
          type="number"
          allowNegativeValue={false}
          allowDecimalValue={false}
          placeholder="0"
          value={draft.position}
          disabled={disabled}
          onChange={(e) => onChange({ position: e.target.value || "0" })}
        />
      </Grid2>
      {isSwatch ? (
        <Grid2 size={{ xs: 12, sm: 2, md: 2 }}>
          <CustomTypography
            variant="body3"
            color={colors?.greyTextColor}
            sx={{ mb: 0.75 }}
          >
            Hex
          </CustomTypography>
          <Grid2 container spacing={1} alignItems="center">
            <Grid2 size={{ xs: 9 }}>
              <CustomTextField
                width="100%"
                placeholder="#FF0000"
                value={draft.hex}
                disabled={disabled}
                onChange={(e) => onChange({ hex: e.target.value || "" })}
              />
            </Grid2>
            <Grid2 size={{ xs: 3 }}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 1,
                  border: `1px solid ${colors?.greyBorder}`,
                  bgcolor: draft.hex?.trim() || colors?.veryLightGray || "#eee",
                }}
              />
            </Grid2>
          </Grid2>
        </Grid2>
      ) : null}
      <Grid2 size={{ xs: 12 }}>
        <Box display="flex" alignItems="center" gap={1}>
          <Switch
            checked={draft.is_active}
            disabled={disabled}
            onChange={(_, checked) => onChange({ is_active: checked })}
            size="small"
          />
          <CustomTypography variant="body3" color={colors?.greyTextColor}>
            {draft.is_active ? "Active" : "Inactive"}
          </CustomTypography>
        </Box>
      </Grid2>
    </Grid2>
  );

  if (!canView) {
    return (
      <Box sx={{ mt: 3, px: 2 }}>
        <Paper sx={{ p: 3, borderRadius: 3 }}>
          <CustomTypography variant="heading3">Catalog Option</CustomTypography>
          <PermissionBlockLabel />
        </Paper>
      </Box>
    );
  }

  if (page.loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "50vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!detail) {
    return (
      <Box sx={{ p: 3 }}>
        <CustomTypography variant="heading6">
          Option not found.
        </CustomTypography>
        <AppButton
          variant="transparentPrimary"
          label="Back to list"
          onClick={() => navigate("/catalog-options/listing")}
          customStyles={{ mt: 2 }}
        />
      </Box>
    );
  }

  const values = detail.values ?? [];

  return (
    <Box
      sx={{
        px: { xs: 2, md: 3 },
        py: 2,
        bgcolor: colors?.greyBodyBackground || "transparent",
        minHeight: "calc(100vh - 80px)",
      }}
    >
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          ...sectionSx,
          background: `linear-gradient(135deg, ${alpha(colors?.purpleMain || theme.palette.primary.main, 0.06)} 0%, ${colors?.white || "#fff"} 55%)`,
        }}
      >
        <Grid2 container spacing={2} columns={12} alignItems="center">
          <Grid2 size={{ xs: 12, md: 8 }}>
            <Grid2 container spacing={1.5} columns={12} alignItems="center">
              <Grid2 size="auto">
                <IconButton
                  onClick={() => navigate("/catalog-options/listing")}
                  size="small"
                  sx={{
                    border: `1px solid ${colors?.greyBorder}`,
                    borderRadius: 2,
                    bgcolor: colors?.white,
                  }}
                >
                  <ArrowBackIcon fontSize="small" />
                </IconButton>
              </Grid2>
              <Grid2 size="grow">
                <Grid2 container spacing={1} columns={12} alignItems="center">
                  <Grid2 size="auto">
                    <CustomTypography variant="heading3">
                      {detail.name}
                    </CustomTypography>
                  </Grid2>
                  <Grid2 size="auto">
                    <Chip
                      size="small"
                      label={page.is_active ? "Active" : "Inactive"}
                      sx={{
                        height: 22,
                        fontSize: "0.7rem",
                        fontWeight: 600,
                        bgcolor: page.is_active
                          ? colors?.successGreenLightBackground
                          : colors?.veryLightGray,
                        color: page.is_active
                          ? colors?.successGreen
                          : colors?.greyTextColor,
                        border: `1px solid ${
                          page.is_active
                            ? colors?.successGreenLightBorder
                            : colors?.greyBorder
                        }`,
                      }}
                    />
                  </Grid2>
                  <Grid2 size="auto">
                    <Chip
                      size="small"
                      label={page.display_type}
                      sx={{
                        height: 22,
                        fontSize: "0.7rem",
                        textTransform: "capitalize",
                        bgcolor:
                          colors?.veryLightPurple ||
                          alpha(theme.palette.primary.main, 0.08),
                        color: colors?.purpleDark || theme.palette.primary.main,
                      }}
                    />
                  </Grid2>
                </Grid2>
                <CustomTypography
                  variant="body3"
                  color={colors?.greyTextColor}
                  sx={{ mt: 0.5 }}
                >
                  Option #{detail.id}
                  {detail.modified_date
                    ? ` · Updated ${formatDate(detail.modified_date)}`
                    : ""}
                  {page.dirty ? " · Unsaved changes" : ""}
                </CustomTypography>
              </Grid2>
            </Grid2>
          </Grid2>

          {canEdit ? (
            <Grid2 size={{ xs: 12, md: 4 }}>
              <Grid2
                container
                spacing={1}
                columns={12}
                justifyContent={{ xs: "flex-start", md: "flex-end" }}
              >
                <Grid2 size="auto">
                  <AppButton
                    variant="transparentPrimary"
                    label="Delete"
                    onClick={() => {
                      page.deleteOptionOpen = true;
                    }}
                    disabled={!detail.is_active || page.savingOption}
                  />
                </Grid2>
                <Grid2 size="auto">
                  <AppButton
                    variant="primary"
                    label={page.savingOption ? "Saving…" : "Save option"}
                    onClick={() => void handleSaveOption()}
                    disabled={page.savingOption || !page.dirty}
                  />
                </Grid2>
              </Grid2>
            </Grid2>
          ) : null}
        </Grid2>
      </Paper>

      {/* Option editor */}
      <Paper elevation={0} sx={sectionSx}>
        <CustomTypography variant="heading6" sx={{ mb: 0.5 }}>
          Option settings
        </CustomTypography>
        <CustomTypography
          variant="body3"
          color={colors?.greyTextColor}
          sx={{ mb: 2.5 }}
        >
          Update name, slug, display type, and position. Changes apply when you
          save.
        </CustomTypography>

        <Grid2 container spacing={2} columns={12}>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <CustomTypography
              variant="body3"
              color={colors?.greyTextColor}
              sx={{ mb: 0.75 }}
            >
              Name *
            </CustomTypography>
            <CustomTextField
              width="100%"
              placeholder="Enter option name"
              value={page.name}
              disabled={!canEdit || page.savingOption}
              onChange={(e) => {
                page.name = e.target.value || "";
                page.markDirty();
              }}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <CustomTypography
              variant="body3"
              color={colors?.greyTextColor}
              sx={{ mb: 0.75 }}
            >
              Slug
            </CustomTypography>
            <CustomTextField
              width="100%"
              placeholder="Auto-generated if left empty on create"
              value={page.slug}
              disabled={!canEdit || page.savingOption}
              onChange={(e) => {
                page.slug = e.target.value || "";
                page.markDirty();
              }}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <CustomTypography
              variant="body3"
              color={colors?.greyTextColor}
              sx={{ mb: 0.75 }}
            >
              Display type *
            </CustomTypography>
            <AppSearchDropdownMulti
              title="Display type"
              isMultiSelect={false}
              local
              list={DISPLAY_TYPE_LIST}
              value={DISPLAY_TYPE_LIST.filter(
                (o) => o.id === page.display_type,
              )}
              disabled={!canEdit || page.savingOption}
              borderColor={colors?.blackBorder}
              borderRadius="8px"
              handleSelect={(items) => {
                const next = items[0]?.id;
                if (next && isDisplayType(next)) {
                  page.display_type = next;
                  page.markDirty();
                }
              }}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <CustomTypography
              variant="body3"
              color={colors?.greyTextColor}
              sx={{ mb: 0.75 }}
            >
              Position *
            </CustomTypography>
            <CustomTextField
              width="100%"
              type="number"
              allowNegativeValue={false}
              allowDecimalValue={false}
              placeholder="0"
              value={page.position}
              disabled={!canEdit || page.savingOption}
              onChange={(e) => {
                page.position = e.target.value || "0";
                page.markDirty();
              }}
            />
          </Grid2>
          <Grid2 size={{ xs: 12 }}>
            <Box display="flex" alignItems="center" gap={1}>
              <Switch
                checked={page.is_active}
                disabled={!canEdit || page.savingOption}
                onChange={(_, checked) => {
                  page.is_active = checked;
                  page.markDirty();
                }}
              />
              <CustomTypography variant="body2">
                {page.is_active ? "Active" : "Inactive"}
              </CustomTypography>
            </Box>
          </Grid2>
        </Grid2>
      </Paper>

      {/* Values editor */}
      <Paper elevation={0} sx={sectionSx}>
        <Grid2
          container
          spacing={1.5}
          columns={12}
          alignItems="center"
          sx={{ mb: 2 }}
        >
          <Grid2 size={{ xs: 12, sm: "grow" }}>
            <CustomTypography variant="heading6">
              Option values
            </CustomTypography>
            <CustomTypography variant="body3" color={colors?.greyTextColor}>
              {values.length} value{values.length === 1 ? "" : "s"} · add, edit,
              or soft-delete inline
            </CustomTypography>
          </Grid2>
          {canEdit && detail.is_active ? (
            <Grid2 size={{ xs: 12, sm: "auto" }}>
              <AppButton
                variant="primary"
                label={page.showAddForm ? "Close form" : "Add value"}
                startIcon={page.showAddForm ? undefined : <AddIcon />}
                onClick={() => {
                  page.showAddForm = !page.showAddForm;
                  page.editingValueId = null;
                  if (page.showAddForm) page.addDraft = emptyDraft();
                }}
                disabled={page.savingValue}
              />
            </Grid2>
          ) : null}
        </Grid2>

        {page.showAddForm && canEdit ? (
          <Box
            sx={{
              mb: 2.5,
              p: 2,
              borderRadius: 2,
              border: `1px dashed ${colors?.purpleMain || theme.palette.primary.main}`,
              bgcolor: alpha(
                colors?.purpleMain || theme.palette.primary.main,
                0.04,
              ),
            }}
          >
            <CustomTypography variant="heading7" sx={{ mb: 1.5 }}>
              New value
            </CustomTypography>
            {renderValueFields(
              page.addDraft,
              (patch) => {
                page.addDraft = { ...page.addDraft, ...patch };
              },
              page.savingValue,
            )}
            <Grid2
              container
              spacing={1}
              columns={12}
              justifyContent="flex-end"
              sx={{ mt: 2 }}
            >
              <Grid2 size="auto">
                <AppButton
                  variant="transparentPrimary"
                  label="Cancel"
                  onClick={() => {
                    page.showAddForm = false;
                    page.addDraft = emptyDraft();
                  }}
                  disabled={page.savingValue}
                />
              </Grid2>
              <Grid2 size="auto">
                <AppButton
                  variant="primary"
                  label={page.savingValue ? "Adding…" : "Add value"}
                  onClick={() => void handleCreateValue()}
                  disabled={page.savingValue}
                />
              </Grid2>
            </Grid2>
          </Box>
        ) : null}

        {values.length === 0 && !page.showAddForm ? (
          <Box
            sx={{
              py: 5,
              textAlign: "center",
              borderRadius: 2,
              border: `1px dashed ${colors?.greyBorder}`,
              bgcolor: colors?.grayBackGroundColor || alpha("#000", 0.02),
            }}
          >
            <CustomTypography variant="body2" color={colors?.greyTextColor}>
              No values yet. Add the first value for this option.
            </CustomTypography>
            {canEdit && detail.is_active ? (
              <AppButton
                variant="primary"
                label="Add value"
                startIcon={<AddIcon />}
                onClick={() => {
                  page.showAddForm = true;
                  page.addDraft = emptyDraft();
                }}
                customStyles={{ mt: 2 }}
              />
            ) : null}
          </Box>
        ) : (
          <Grid2 container spacing={1.5} columns={12}>
            {values.map((row) => {
              const hex = getMetaHex(row.meta);
              const isEditing = page.editingValueId === row.id;

              return (
                <Grid2 size={{ xs: 12 }} key={row.id}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: `1px solid ${
                        isEditing
                          ? colors?.purpleMain || theme.palette.primary.main
                          : colors?.greyBorder
                      }`,
                      bgcolor: isEditing
                        ? alpha(
                            colors?.purpleMain || theme.palette.primary.main,
                            0.03,
                          )
                        : colors?.cardBackground || colors?.white,
                      transition:
                        "border-color 0.2s ease, box-shadow 0.2s ease",
                      boxShadow: isEditing ? colors?.shadowLight : "none",
                      opacity: row.is_active ? 1 : 0.72,
                    }}
                  >
                    {isEditing ? (
                      <>
                        <Grid2
                          container
                          spacing={1}
                          columns={12}
                          alignItems="center"
                          sx={{ mb: 1.5 }}
                        >
                          <Grid2 size="grow">
                            <CustomTypography variant="heading7">
                              Edit value
                            </CustomTypography>
                          </Grid2>
                          <Grid2 size="auto">
                            <Tooltip title="Cancel">
                              <IconButton
                                size="small"
                                onClick={cancelEditValue}
                                disabled={page.savingValue}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Save">
                              <IconButton
                                size="small"
                                onClick={() => void handleUpdateValue(row.id)}
                                disabled={page.savingValue}
                                sx={{ color: colors?.successGreen }}
                              >
                                <CheckIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Grid2>
                        </Grid2>
                        {renderValueFields(
                          page.editDraft,
                          (patch) => {
                            page.editDraft = { ...page.editDraft, ...patch };
                          },
                          page.savingValue,
                        )}
                        <Grid2
                          container
                          spacing={1}
                          columns={12}
                          justifyContent="flex-end"
                          sx={{ mt: 2 }}
                        >
                          <Grid2 size="auto">
                            <AppButton
                              variant="transparentPrimary"
                              label="Cancel"
                              onClick={cancelEditValue}
                              disabled={page.savingValue}
                            />
                          </Grid2>
                          <Grid2 size="auto">
                            <AppButton
                              variant="primary"
                              label={
                                page.savingValue ? "Saving…" : "Save value"
                              }
                              onClick={() => void handleUpdateValue(row.id)}
                              disabled={page.savingValue}
                            />
                          </Grid2>
                        </Grid2>
                      </>
                    ) : (
                      <Grid2
                        container
                        spacing={1.5}
                        columns={12}
                        alignItems="center"
                      >
                        <Grid2 size={{ xs: 12, sm: "grow" }}>
                          <Grid2
                            container
                            spacing={1.5}
                            columns={12}
                            alignItems="center"
                          >
                            {hex ? (
                              <Grid2 size="auto">
                                <Box
                                  sx={{
                                    width: 28,
                                    height: 28,
                                    borderRadius: 1.5,
                                    bgcolor: hex,
                                    border: `1px solid ${colors?.greyBorder}`,
                                  }}
                                />
                              </Grid2>
                            ) : null}
                            <Grid2 size="grow">
                              <CustomTypography variant="heading7">
                                {row.value}
                              </CustomTypography>
                              <CustomTypography
                                variant="body3"
                                color={colors?.greyTextColor}
                              >
                                SKU {row.sku_code || "—"} · Pos {row.position}
                                {hex ? ` · ${hex}` : ""}
                              </CustomTypography>
                            </Grid2>
                            <Grid2 size="auto">
                              <Chip
                                size="small"
                                label={row.is_active ? "Active" : "Inactive"}
                                sx={{
                                  height: 20,
                                  fontSize: "0.65rem",
                                  bgcolor: row.is_active
                                    ? colors?.successGreenLightBackground
                                    : colors?.veryLightGray,
                                  color: row.is_active
                                    ? colors?.successGreen
                                    : colors?.greyTextColor,
                                }}
                              />
                            </Grid2>
                          </Grid2>
                        </Grid2>

                        {canEdit ? (
                          <Grid2 size={{ xs: 12, sm: "auto" }}>
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={() => startEditValue(row)}
                                disabled={page.savingValue || page.showAddForm}
                                sx={{
                                  border: `1px solid ${colors?.greyBorder}`,
                                  borderRadius: 1.5,
                                  mr: 0.5,
                                }}
                              >
                                <EditOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Soft delete">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    page.deletingValue = row;
                                    page.deleteValueOpen = true;
                                  }}
                                  disabled={!row.is_active || page.savingValue}
                                  sx={{
                                    border: `1px solid ${colors?.greyBorder}`,
                                    borderRadius: 1.5,
                                    color: colors?.errorRed,
                                  }}
                                >
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Grid2>
                        ) : null}
                      </Grid2>
                    )}
                  </Box>
                </Grid2>
              );
            })}
          </Grid2>
        )}
      </Paper>

      {page.deleteOptionOpen ? (
        <ConfirmationPopup
          id={detail.id}
          open={page.deleteOptionOpen}
          title="Delete Option"
          content={`Soft-delete "${detail.name}"? Its values will also be deactivated.`}
          onConfirm={() => {
            void handleDeleteOption();
          }}
          onCancel={() => {
            page.deleteOptionOpen = false;
          }}
        />
      ) : null}

      {page.deleteValueOpen && page.deletingValue ? (
        <ConfirmationPopup
          id={page.deletingValue.id}
          open={page.deleteValueOpen}
          title="Delete Value"
          content={`Soft-delete value "${page.deletingValue.value}"?`}
          onConfirm={() => {
            void handleDeleteValue();
          }}
          onCancel={() => {
            page.deleteValueOpen = false;
            page.deletingValue = null;
          }}
        />
      ) : null}
    </Box>
  );
};

export default observer(CatalogOptionDetail);
